import { useEffect, useRef, useState } from 'react';
import { generatePath, useLocation, useParams } from 'react-router';

import { ActionsApi } from 'Api/Actions/ActionsApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { ActionHistoryTable, ActionHistoryTableProps } from 'Components/Actions/ActionHistoryTable/ActionHistoryTable';
import { Button, Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { AssociatedControlsModal, AssociatedControlsModalProps } from 'Components/Modal/AssociatedControlsModal/AssociatedControlsModal';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { RiskAccordion } from 'Components/RiskRegister/RiskAccordion/RiskAccordion';
import { PrimaryTabs, Tab } from 'Components/Tabs/PrimaryTabs/PrimaryTabs';
import { Text } from 'Components/Text/Text';
import { ICON_DOWNLOAD, ICON_EDIT_MODIFY_UPDATE } from 'Config/Icons';
import { ACTION_LISTING, UPDATE_ACTION } from 'Config/PathTemplates';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { downloadElementAsPdf } from 'Helpers/PdfUtils';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { Action, ActionHistory } from 'Models/Actions';
import { MappedRisksModal, MappedRisksModalProps } from 'Pages/ActionPlanning/ActionsListing/MappedRisksModal/MappedRisksModal';
import styles from 'Styles/DetailsPage.module.css';

import { ActionDetailsSnapshot } from './ActionDetailsSnapshot';
import { ActionHistoryModal } from './ActionHistoryModal';

export interface ActionsDetailsProps {
    authHandler: AuthHandler;
    documentApi: DocumentApi;
    actionsApi: ActionsApi;
}

interface UrlParams {
    action_id: string;
}

enum ActionTab {
    RISKS = 'RISKS',
    HISTORY = 'history',
}

enum Modals {
    AssociatedControlsModal,
    MappedRisksModal,
    HistoryModal,
    None,
}

export const ActionsDetails = (props: ActionsDetailsProps) => {
    const cachedData = useCachedData();
    const params = useParams<keyof UrlParams>() as UrlParams;
    const location = useLocation();
    const printPdfRef = useRef<HTMLDivElement>(null);

    const [action, setAction] = useState<Action>();
    const [actionHistory, setActionHistory] = useState<ActionHistory[]>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const [selectedActionHistory, setSelectedActionHistory] = useState<ActionHistory>();
    const [selectedRiskIndex, setSelectedRiskIndex] = useState<number>();
    const [displayedModal, setDisplayedModal] = useState<Modals>(Modals.None);

    const activeHash = (() => {
        if (location.hash && location.hash.length > 0) {
            const strippedHash = location.hash.substring(1);
            if (Object.values(ActionTab).includes(strippedHash as ActionTab)) {
                return strippedHash;
            }
        }
        return ActionTab.RISKS;
    })();

    useEffect(() => {
        const getActionDetails = async (): Promise<void> => {
            try {
                const actionsResponse = await props.actionsApi.getActionDetails(params.action_id);
                setAction(actionsResponse.data);
            } catch (error) {
                setErrorMessage(error.message);
            }
        };
        getActionDetails();
    }, [params.action_id, props.actionsApi]);

    useEffect(() => {
        const getActionHistories = async (): Promise<void> => {
            try {
                const historiesResponse = await props.actionsApi.getActionHistory(params.action_id);
                setActionHistory(historiesResponse.data);
            } catch (error) {
                setErrorMessage(error.message);
            }
        };
        getActionHistories();
    }, [params.action_id, props.actionsApi]);

    /** Convert a component to a PDF and download it. */
    const handleDownloadPdf = async () => {
        const elementToPrint = printPdfRef.current;
        if (!elementToPrint) {
            return;
        }
        const filename = `${action?.title}.pdf`;
        const loggedInUser = props.authHandler.getAuthenticatedUserFullName(cachedData.users);
        await downloadElementAsPdf(elementToPrint, filename, loggedInUser);
    };

    const showAssociatedControlsModal = (index: number) => {
        setSelectedRiskIndex(index);
        setDisplayedModal(Modals.AssociatedControlsModal);
    };

    const showMappedRisksModal = (actionHistory: ActionHistory) => {
        setSelectedActionHistory(actionHistory);
        setDisplayedModal(Modals.MappedRisksModal);
    };

    const showHistoryModal = (actionHistory: ActionHistory) => {
        setSelectedActionHistory(actionHistory);
        setDisplayedModal(Modals.HistoryModal);
    };

    const associatedControlsModalProps: AssociatedControlsModalProps | undefined =
        action && selectedRiskIndex !== undefined
            ? {
                  hideModal: () => setDisplayedModal(Modals.None),
                  associatedControls: action.associated_risks[selectedRiskIndex].controls.map((controlMapping) => controlMapping.control),
              }
            : undefined;

    const mappedRisksModalProps: MappedRisksModalProps | undefined =
        selectedActionHistory !== undefined
            ? {
                  hideModal: () => setDisplayedModal(Modals.None),
                  associatedRisks: selectedActionHistory.associated_risks,
              }
            : undefined;

    if (action && actionHistory) {
        const actionHistoryTableProps: ActionHistoryTableProps = {
            histories: actionHistory,
            onSelectHistory: showHistoryModal,
            showRisks: showMappedRisksModal,
        };

        return (
            <>
                {displayedModal === Modals.HistoryModal && selectedActionHistory && <ActionHistoryModal history={selectedActionHistory} documentApi={props.documentApi} hideModal={() => setDisplayedModal(Modals.None)} />}
                {displayedModal === Modals.AssociatedControlsModal && associatedControlsModalProps && <AssociatedControlsModal {...associatedControlsModalProps} />}
                {displayedModal === Modals.MappedRisksModal && mappedRisksModalProps && <MappedRisksModal {...mappedRisksModalProps} />}
                <div ref={printPdfRef}>
                    <PageLayoutDefault
                        headerBreadcrumb={
                            <Breadcrumb textColor="blue">
                                <BreadcrumbLink link={ACTION_LISTING}>Action Planning</BreadcrumbLink>
                                <BreadcrumbText>{action.title}</BreadcrumbText>
                            </Breadcrumb>
                        }
                        headerTitle={action.title}
                        headerButtons={
                            <>
                                <Button variant="secondary" onClick={handleDownloadPdf} fontAwesomeImage={ICON_DOWNLOAD}>
                                    Download
                                </Button>
                                <Link variant="primaryButton" to={generatePath(UPDATE_ACTION, { action_id: params.action_id })} fontAwesomeImage={ICON_EDIT_MODIFY_UPDATE}>
                                    Manage Action
                                </Link>
                            </>
                        }
                        headerDescription={`Last updated: ${iso8601ToUsDateShort(action.last_updated)} by ${getUserNameFromSubject(action.last_updated_by, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}`}
                        body={[
                            {
                                title: 'Action Details',
                                content: <ActionDetailsSnapshot type="current" action={action} documentApi={props.documentApi} />,
                            },
                            {
                                content: (
                                    <PrimaryTabs defaultActiveTab={activeHash} removePadding transparent>
                                        <Tab eventKey={ActionTab.RISKS} title="Risks">
                                            <div className={styles.tabContent}>
                                                <RiskAccordion risks={action.associated_risks} showModal={(index) => showAssociatedControlsModal(index)} />
                                            </div>
                                        </Tab>
                                        <Tab eventKey={ActionTab.HISTORY} title="History">
                                            <div className={styles.tabContent} data-testid="issueHistoryTable">
                                                <ActionHistoryTable {...actionHistoryTableProps} />
                                            </div>
                                        </Tab>
                                    </PrimaryTabs>
                                ),
                            },
                        ]}
                    />
                </div>
            </>
        );
    } else if (errorMessage) {
        return <Text>{errorMessage}</Text>;
    } else {
        return <Placeholder />;
    }
};
